<template>
    <Layout>
        <PageHeader :title="$t('module.notification')" :items="breadcrumbs" />
        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-4 mb-2 mb-lg-0">
                        <b-input-group>
                            <b-form-input v-model.trim="searchText" type="search" :placeholder="$t('label.search')"
                                autocomplete="off" @keyup.enter="applyFilters" />
                            <b-input-group-append>
                                <b-button variant="info" @click="applyFilters">
                                    <i class="mdi mdi-magnify"></i>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                    <div class="col-lg-8 d-flex justify-content-lg-end">
                        <b-dropdown variant="warning" no-caret right boundary="viewport"
                            menu-class="notification-filter-menu">
                            <template #button-content><i class="mdi mdi-filter"></i></template>
                            <div class="px-3 py-2">
                                <b-form-group :label="$t('label.notificationType')" label-class="small text-muted">
                                    <b-form-select v-model="notificationType" :options="typeOptions" />
                                </b-form-group>
                                <div class="d-flex justify-content-end">
                                    <b-button size="sm" variant="outline-secondary" @click="resetFilters">
                                        {{ $t('label.reset') }}
                                    </b-button>
                                    <b-button size="sm" variant="info" class="ml-2" @click="applyFilters">
                                        {{ $t('label.search') }}
                                    </b-button>
                                </div>
                            </div>
                        </b-dropdown>
                    </div>
                </div>

                <div v-if="!status.list" class="d-flex justify-content-center p-5">
                    <b-spinner variant="info" :label="$t('label.loading')"></b-spinner>
                </div>
                <template v-else>
                    <b-table :items="tableRows" :fields="fields" show-empty responsive no-local-sorting
                        @sort-changed="changeSort">
                        <template #cell(type)="{ item }">
                            <b-badge :variant="item.type === 1 ? 'primary' : 'info'">
                                {{ typeName(item.type) }}
                            </b-badge>
                        </template>
                        <template #cell(isActive)="{ item }">
                            <b-badge :variant="item.isActive ? 'success' : 'secondary'">
                                {{ item.isActive ? $t('label.active') : $t('label.inactive') }}
                            </b-badge>
                        </template>
                        <template #cell(icon)="{ item }">
                            <span v-if="item.icon">{{ item.icon.name }}</span>
                            <span v-else>-</span>
                        </template>
                        <template #cell(actions)="{ item }">
                            <a href="javascript:void(0);" class="text-info" v-b-tooltip.hover
                                :title="$t('label.viewNotificationDetails')"
                                @click.stop="openForm(item)">
                                <i class="mdi mdi-eye-outline font-size-18"></i>
                            </a>
                            <a v-if="item.type === 0" href="javascript:void(0);" class="text-primary ml-2"
                                v-b-tooltip.hover :title="$t('label.assignNotification')"
                                @click.stop="assignNotification(item)">
                                <i class="mdi mdi-account-plus font-size-18"></i>
                            </a>
                            <a v-if="item.type === 0" href="javascript:void(0);" class="text-danger ml-2"
                                v-b-tooltip.hover :title="$t('label.deleteX', [$t('module.notification')])"
                                @click.stop="deleteNotification(item)">
                                <i class="mdi mdi-delete-outline font-size-18"></i>
                            </a>
                        </template>
                    </b-table>

                    <div v-if="pagination.total"
                        class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
                        <small class="text-muted mb-2 mb-md-0">
                            {{ $tc('label.results', pagination.total, [pagination.total]) }}
                        </small>
                        <b-pagination v-model="currentPage" :total-rows="pagination.total"
                            :per-page="searchCriteria.pageSize" class="mb-0" @change="pageNavigation" />
                    </div>
                </template>
            </div>
        </div>
        <b-modal id="notification-form" :title="$t('label.notificationDetails')"
            size="xl" hide-footer hide-header-close centered @hidden="closeForm">
            <b-container fluid>
                <NotificationForm v-if="formVisible" :notification-u-u-i-d="notification.uuid"
                    :search-criteria="searchCriteria" @done="closeForm" />
            </b-container>
        </b-modal>
    </Layout>
</template>

<script>
import { mapState } from 'vuex';
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import i18n from '@/i18n';
import { SESSION } from '@/constants';
import { sessionHelper } from '@/utils';
import { notificationMethods } from '@/state/api';
import { NOTIFICATION_MODULE } from '@/state/api/notification.state';
import NotificationForm from '@/views/pages/admin/notification/form';


const defaultCriteria = () => ({
    pageNumber: 1,
    pageSize: 15,
    searchArray: [],
    type: '',
    sortBy: 'created_at',
    orderBy: 'desc',
});

export default {
    name: 'NotificationListing',
    page: { title: i18n.t('module.notification') },
    components: { Layout, PageHeader, NotificationForm },
    data() {
        return {
            searchText: '',
            notificationType: '',
            currentPage: 1,
            formVisible: false,
            notification: { uuid: '' },
            searchCriteria: defaultCriteria(),
            typeOptions: [
                { value: '', text: i18n.t('label.all') },
                { value: 0, text: i18n.t('label.custom') },
                { value: 1, text: i18n.t('label.system') },
            ],
            breadcrumbs: [
                { text: i18n.t('module.communication'), href: '/communications' },
                { text: i18n.t('module.notification'), active: true },
            ],
            fields: [
                { key: 'name', label: i18n.t('label.name'), sortable: true, class: 'text-nowrap' },
                { key: 'icon', label: i18n.t('label.icon'), sortable: false, class: 'text-nowrap' },
                { key: 'title', label: i18n.t('label.title'), sortable: true },
                { key: 'type', label: i18n.t('label.type'), sortable: true, class: 'text-nowrap' },
                { key: 'isActive', label: i18n.t('label.status'), sortable: false, class: 'text-nowrap' },
                { key: 'updatedAt', label: i18n.t('label.updatedAt'), sortable: true, class: 'text-nowrap' },
                { key: 'actions', label: i18n.t('label.action'), sortable: false, class: 'text-nowrap' },
            ],
        };
    },
    computed: {
        ...mapState(NOTIFICATION_MODULE, ['status']),
        tableRows() {
            return this.$store.state.api.notification.list.data || [];
        },
        pagination() {
            return this.$store.state.api.notification.list.pagination || {};
        },
    },
    created() {
        this.restoreFilters();
        this.loadNotifications();
    },
    methods: {
        ...notificationMethods,
        restoreFilters() {
            if (sessionHelper.hasSearchCriteria(SESSION.NOTIFICATION_SEARCH)) {
                this.searchCriteria = {
                    ...defaultCriteria(),
                    ...sessionHelper.retrieveSearch(SESSION.NOTIFICATION_SEARCH),
                };
            }
            this.currentPage = this.searchCriteria.pageNumber;
            this.searchText = this.searchCriteria.searchArray[0] || '';
            this.notificationType = this.searchCriteria.type;
        },
        async loadNotifications() {
            sessionHelper.storeSearch(SESSION.NOTIFICATION_SEARCH, this.searchCriteria);
            await this.getAllNotification({ filter: this.searchCriteria });
        },
        applyFilters() {
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                searchArray: this.searchText ? [this.searchText] : [],
                type: this.notificationType,
            };
            this.loadNotifications();
        },
        resetFilters() {
            this.searchText = '';
            this.notificationType = '';
            this.currentPage = 1;
            this.searchCriteria = defaultCriteria();
            this.loadNotifications();
        },
        pageNavigation(pageNumber) {
            this.currentPage = pageNumber;
            this.searchCriteria = { ...this.searchCriteria, pageNumber };
            this.loadNotifications();
        },
        changeSort({ sortBy, sortDesc }) {
            const apiFields = {
                name: 'name',
                title: 'title',
                type: 'type',
                createdAt: 'created_at',
            };
            if (!apiFields[sortBy]) return;
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                sortBy: apiFields[sortBy],
                orderBy: sortDesc ? 'desc' : 'asc',
            };
            this.loadNotifications();
        },
        typeName(type) {
            return type === 1 ? this.$t('label.system') : this.$t('label.custom');
        },
        openForm(notification = null) {
            this.notification = { uuid: notification?.uuid || '' };
            this.formVisible = true;
            this.$nextTick(() => this.$bvModal.show('notification-form'));
        },
        closeForm() {
            this.$bvModal.hide('notification-form');
            this.formVisible = false;
            this.notification = { uuid: '' };
        },
        assignNotification(notification) {
            this.$emit('assign-notification', notification);
        },
        deleteNotification(notification) {
            this.$emit('delete-notification', notification);
        },
    },
};
</script>

<style scoped>
::v-deep .notification-filter-menu {
    min-width: 300px;
    max-width: calc(100vw - 2rem);
}
</style>
